import { StateCreator } from 'zustand';
import { CartStore, ScheduledCartSlice } from './types';

export const createScheduledCartSlice: StateCreator<CartStore, [], [], ScheduledCartSlice> = (set, get) => ({
    scheduledOrders: [],

    addScheduledItem: (product, scheduledDate, quantity = 1, options) => {
        const scheduledOrders = get().scheduledOrders;
        const dateOrder = scheduledOrders.find(order => order.scheduledDate === scheduledDate);
        const observation = options?.observation || '';
        const sub_products = options?.sub_products || [];

        // Generate unique ID for this specific product + sub-product combination
        const cartItemId = sub_products.length > 0 
            ? `${product.uuid}|${sub_products.map(s => s.sub_product_uuid).sort().join(',')}` 
            : product.uuid;

        if (dateOrder) {
            const existingItem = dateOrder.items.find(item => item.cartItemId === cartItemId);

            if (existingItem) {
                set({
                    scheduledOrders: scheduledOrders.map(order =>
                        order.scheduledDate === scheduledDate
                            ? {
                                ...order,
                                items: order.items.map(item =>
                                    item.cartItemId === cartItemId
                                        ? { ...item, quantity: item.quantity + quantity, observation: observation || item.observation }
                                        : item
                                ),
                            }
                            : order
                    ),
                });
            } else {
                set({
                    scheduledOrders: scheduledOrders.map(order =>
                        order.scheduledDate === scheduledDate
                            ? {
                                ...order,
                                items: [...order.items, { cartItemId, product, quantity, type: 'product', observation, sub_products }],
                            }
                            : order
                    ),
                });
            }
        } else {
            set({
                scheduledOrders: [
                    ...scheduledOrders,
                    {
                        scheduledDate,
                        items: [{ cartItemId, product, quantity, type: 'product', observation, sub_products }],
                        offers: [],
                    },
                ],
            });
        }
    },

    addScheduledOffer: (offer, scheduledDate, quantity = 1) => {
        const scheduledOrders = get().scheduledOrders;
        const dateOrder = scheduledOrders.find(order => order.scheduledDate === scheduledDate);

        if (dateOrder) {
            const existingOffer = dateOrder.offers.find(item => item.offer.uuid === offer.uuid);

            if (existingOffer) {
                set({
                    scheduledOrders: scheduledOrders.map(order =>
                        order.scheduledDate === scheduledDate
                            ? {
                                ...order,
                                offers: order.offers.map(item =>
                                    item.offer.uuid === offer.uuid
                                        ? { ...item, quantity: item.quantity + quantity }
                                        : item
                                ),
                            }
                            : order
                    ),
                });
            } else {
                set({
                    scheduledOrders: scheduledOrders.map(order =>
                        order.scheduledDate === scheduledDate
                            ? {
                                ...order,
                                offers: [...order.offers, { offer, quantity, type: 'offer' }],
                            }
                            : order
                    ),
                });
            }
        } else {
            set({
                scheduledOrders: [
                    ...scheduledOrders,
                    {
                        scheduledDate,
                        items: [],
                        offers: [{ offer, quantity, type: 'offer' }],
                    },
                ],
            });
        }
    },

    removeScheduledItem: (cartItemId, scheduledDate) => {
        set({
            scheduledOrders: get().scheduledOrders.map(order =>
                order.scheduledDate === scheduledDate
                    ? {
                        ...order,
                        items: order.items.filter(item => item.cartItemId !== cartItemId),
                    }
                    : order
            ).filter(order => order.items.length > 0 || order.offers.length > 0),
        });
    },

    removeScheduledOffer: (offerUuid, scheduledDate) => {
        set({
            scheduledOrders: get().scheduledOrders.map(order =>
                order.scheduledDate === scheduledDate
                    ? {
                        ...order,
                        offers: order.offers.filter(item => item.offer.uuid !== offerUuid),
                    }
                    : order
            ).filter(order => order.items.length > 0 || order.offers.length > 0),
        });
    },

    updateScheduledQuantity: (cartItemId, scheduledDate, quantity) => {
        if (quantity <= 0) {
            get().removeScheduledItem(cartItemId, scheduledDate);
            return;
        }

        set({
            scheduledOrders: get().scheduledOrders.map(order =>
                order.scheduledDate === scheduledDate
                    ? {
                        ...order,
                        items: order.items.map(item =>
                            item.cartItemId === cartItemId
                                ? { ...item, quantity }
                                : item
                        ),
                    }
                    : order
            ),
        });
    },

    updateScheduledOfferQuantity: (offerUuid, scheduledDate, quantity) => {
        if (quantity <= 0) {
            get().removeScheduledOffer(offerUuid, scheduledDate);
            return;
        }

        set({
            scheduledOrders: get().scheduledOrders.map(order =>
                order.scheduledDate === scheduledDate
                    ? {
                        ...order,
                        offers: order.offers.map(item =>
                            item.offer.uuid === offerUuid
                                ? { ...item, quantity }
                                : item
                        ),
                    }
                    : order
            ),
        });
    },

    updateScheduledItemObservation: (cartItemId, scheduledDate, observation) => {
        set({
            scheduledOrders: get().scheduledOrders.map(order =>
                order.scheduledDate === scheduledDate
                    ? {
                        ...order,
                        items: order.items.map(item =>
                            item.cartItemId === cartItemId
                                ? { ...item, observation }
                                : item
                        ),
                    }
                    : order
            ),
        });
    },

    updateScheduledItemSubProducts: (cartItemId, scheduledDate, sub_products) => {
        set({
            scheduledOrders: get().scheduledOrders.map(order =>
                order.scheduledDate === scheduledDate
                    ? {
                        ...order,
                        items: (() => {
                            const itemToUpdate = order.items.find(i => i.cartItemId === cartItemId);
                            if (!itemToUpdate) return order.items;

                            const newCartItemId = sub_products.length > 0 
                                ? `${itemToUpdate.product.uuid}|${sub_products.map(s => s.sub_product_uuid).sort().join(',')}` 
                                : itemToUpdate.product.uuid;

                            const existingWithNewId = order.items.find(i => i.cartItemId === newCartItemId && i.cartItemId !== cartItemId);

                            if (existingWithNewId) {
                                return order.items
                                    .filter(i => i.cartItemId !== cartItemId)
                                    .map(i => i.cartItemId === newCartItemId 
                                        ? { ...i, quantity: i.quantity + itemToUpdate.quantity, sub_products } 
                                        : i);
                            } else {
                                return order.items.map(item =>
                                    item.cartItemId === cartItemId ? { ...item, cartItemId: newCartItemId, sub_products } : item
                                );
                            }
                        })()
                    }
                    : order
            ),
        });
    },

    updateScheduledOfferObservation: (offerUuid, scheduledDate, observation) => {
        set({
            scheduledOrders: get().scheduledOrders.map(order =>
                order.scheduledDate === scheduledDate
                    ? {
                        ...order,
                        offers: order.offers.map(item =>
                            item.offer.uuid === offerUuid
                                ? { ...item, observation }
                                : item
                        ),
                    }
                    : order
            ),
        });
    },

    setScheduledNotes: (scheduledDate, notes) => {
        set({
            scheduledOrders: get().scheduledOrders.map(order =>
                order.scheduledDate === scheduledDate
                    ? { ...order, notes }
                    : order
            ),
        });
    },

    removeScheduledDate: (scheduledDate) => {
        set({
            scheduledOrders: get().scheduledOrders.filter(order => order.scheduledDate !== scheduledDate),
        });
    },

    clearScheduledOrders: () => {
        set({ scheduledOrders: [] });
    },

    getTotalByDate: (scheduledDate) => {
        const dateOrder = get().scheduledOrders.find(order => order.scheduledDate === scheduledDate);
        if (!dateOrder) return 0;

        const itemsTotal = dateOrder.items.reduce((sum, item) => {
            const productPrice = item.product.price;
            const subProductsPrice = item.sub_products?.reduce((acc, sub) => acc + sub.additional_price, 0) || 0;
            return sum + ((productPrice + subProductsPrice) * item.quantity);
        }, 0);

        const offersTotal = dateOrder.offers.reduce((sum, item) => sum + (item.offer.price * item.quantity), 0);
        return itemsTotal + offersTotal;
    },

    getScheduledDates: () => {
        return get().scheduledOrders
            .map(order => order.scheduledDate)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    },

    hasScheduledOrders: () => {
        return get().scheduledOrders.length > 0;
    },
});
